<script setup lang="ts">
import type { FlowLayoutSize } from "@/components/controls/BmFormFlowLayout.vue";
import BmFormFlowLayout, {
  flowLayoutSizes,
} from "@/components/controls/BmFormFlowLayout.vue";
import BmEnumDdl from "@/components/controls/input/BmEnumDdl.vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import BmDateQ from "@/components/controls/input/BmDateQ.vue";
import BmTime from "@/components/controls/input/BmTime.vue";
import BmSwitch from "@/components/controls/input/BmSwitch.vue";
import BmNumberQ from "@/components/controls/input/BmNumberQ.vue";
import BmTextArea from "@/components/controls/input/BmTextArea.vue";
import type { PlannedSalesVisit } from "@/app/services/generated/salesvisitService";
import { computed, onBeforeMount, watch } from "vue";
import { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import {
  getSalesVisitEntityMapping,
  getSalesVisitEntityOptions,
} from "@/components/pages/orders/salesvisits/useSalesVisitData";
import {
  getRepeatBasisText,
  repeatBasisNeedsParameter,
  repeatBasisParameterMax,
} from "@/business_logic/tasks/taskUtils";
import { userService } from "@/app/services/extensions/userServiceExtended";
import api from "@/app/api";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { User } from "@/app/services/generated/userService";
import { keys } from "@/plugins/i18n";

import { buildCache } from "@/app/caching/buildCache";
import BmText from "@/components/controls/input/BmText.vue";
import BmAddress from "@/components/controls/address/BmAddress.vue";
import { ruleSet } from "@/plugins/validation";
import type BaseEnumType from "@/app/services/baseEnumType";

interface PlannedSalesVisitHeaderProps {
  isEditing?: boolean;
  value: PlannedSalesVisit;
  headerSize?: FlowLayoutSize;
  showEntity?: boolean;
  showUser?: boolean;
  showDate?: boolean;
}

interface PlannedSalesVisitHeaderEvents {
  (eventName: "update:value", salesVisit: PlannedSalesVisit): void;
}

const props = withDefaults(defineProps<PlannedSalesVisitHeaderProps>(), {
  isEditing: false,
  headerSize: "full-width",
  showEntity: true,
  showUser: true,
  showDate: true,
});

const emit = defineEmits<PlannedSalesVisitHeaderEvents>();

const internalSalesVisit = computed({
  get: () => props.value,
  set: (newSalesVisit: PlannedSalesVisit) => {},
});
const users = makeRef<readonly User[]>([]);

const cache = buildCache()
  .withOutletSummaries()
  .withGroups()
  .withLeads()
  .build();

const entityTypeMapping = computed(() =>
  getSalesVisitEntityMapping(internalSalesVisit.value.associatedEntityType)
);

const headerSizeDefinition = computed(() => {
  return flowLayoutSizes[props.headerSize];
});

const maxRepeatParameter = computed(() =>
  repeatBasisParameterMax(internalSalesVisit.value.repeatBasis)
);
const repeatParameterIsRelevant = computed(() =>
  repeatBasisNeedsParameter(internalSalesVisit.value.repeatBasis)
);

const entityTypeChanged = (
  val:
    | BaseEnumType<EnumSalesVisitAssociatedEntityType>
    | BaseEnumType<EnumSalesVisitAssociatedEntityType>[]
    | null
) => {
  const v =
    val === null || Array.isArray(val)
      ? EnumSalesVisitAssociatedEntityType.none
      : (val as EnumSalesVisitAssociatedEntityType);
  internalSalesVisit.value.associatedEntityType = v;
  if (v.isNone()) {
    internalSalesVisit.value.associatedEntityId = null;
  }
};

async function loadDependencies() {
  [users.value] = await Promise.all([
    userService.getAllUsersWithPremierUsersHidden(api.tenantGroupId),
    cache.update(),
  ]);
}
onBeforeMount(loadDependencies);

watch(
  () => props.value,
  () => {
    internalSalesVisit.value = props.value.clone();
  }
);

watch(internalSalesVisit, () => {
  emit("update:value", internalSalesVisit.value);
});
</script>

<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col>
        <bm-form-flow-layout v-bind="headerSizeDefinition">
          <bm-enum-ddl
            v-if="showEntity"
            :value="internalSalesVisit.associatedEntityType"
            :t="$m.keys.sales_visit_associated_type"
            :enum-type="EnumSalesVisitAssociatedEntityType"
            :read-only="!isEditing"
            :rules="[$ruleSet.required()]"
            @update:value="entityTypeChanged($event)"
          />
          <bm-dropdown
            v-if="
              showEntity && !internalSalesVisit.associatedEntityType.isNone()
            "
            :value.sync="internalSalesVisit.associatedEntityId"
            :t="entityTypeMapping.typeNameKey"
            :read-only="!isEditing"
            :items="
              getSalesVisitEntityOptions(
                internalSalesVisit.associatedEntityType,
                cache
              )
            "
            item-value="id"
            item-text="name"
            :rules="[$ruleSet.required()]"
            :router-location="
              entityTypeMapping.viewLocation(
                internalSalesVisit.associatedEntityId
              )
            "
          />
          <bm-text
            v-if="internalSalesVisit.associatedEntityType.isNone()"
            :value.sync="internalSalesVisit.adHocBusinessName"
            :t="$m.keys.distribution_business_name"
            :rules="[ruleSet.required()]"
            :read-only="!isEditing"
          />
          <bm-address
            v-if="internalSalesVisit.associatedEntityType.isNone()"
            :t="$m.keys.address"
            :address-line1.sync="internalSalesVisit.adHocAddressLine1"
            :address-line2.sync="internalSalesVisit.adHocAddressLine2"
            :address-line3.sync="internalSalesVisit.adHocAddressLine3"
            :address-line4.sync="internalSalesVisit.adHocAddressLine4"
            :address-line5.sync="internalSalesVisit.adHocAddressLine5"
            :address-line6.sync="internalSalesVisit.adHocAddressLine6"
            :address-postcode.sync="internalSalesVisit.adHocAddressPostcode"
            :read-only="!isEditing"
          />
          <bm-dropdown
            v-if="showUser"
            :value.sync="internalSalesVisit.userId"
            :t="keys.sales_visits_user"
            :items="users"
            :read-only="!isEditing"
            item-value="id"
            item-text="name"
            :rules="[$ruleSet.required()]"
          />
          <bm-date-q
            v-if="showDate"
            :value.sync="internalSalesVisit.date"
            :t="keys.sales_visit_date"
            :read-only="!isEditing"
            :rules="[$ruleSet.required()]"
          />
          <bm-time
            :value.sync="internalSalesVisit.plannedTime"
            :t="keys.sales_visit_planned_time"
            :read-only="!isEditing"
          />
          <bm-switch
            :value.sync="internalSalesVisit.isPlannedTimeFlexible"
            :t="keys.sales_visit_is_planned_start_time_flexible"
            :read-only="!isEditing"
          />
          <bm-number-q
            :value.sync="internalSalesVisit.durationMinutes"
            :t="keys.sales_visit_duration"
            :read-only="!isEditing"
            :min="0"
            :rules="[$ruleSet.notNegativeNumber()]"
            :suffix="$m.units_mins_suffix"
            integral
          />
          <bm-text-area
            :value.sync="internalSalesVisit.notes"
            :t="keys.sales_visit_notes"
            :read-only="!isEditing"
          />
          <bm-dropdown
            :value.sync="internalSalesVisit.repeatBasis"
            :t="keys.sales_visit_repeat_basis"
            :read-only="!isEditing"
            :items="EnumTaskRepeatBasis.getValues()"
            :item-text="
              (basis) =>
                getRepeatBasisText({
                  repeatBasis: basis,
                  date: internalSalesVisit.date,
                  repeatParameter: repeatParameterIsRelevant
                    ? internalSalesVisit.repeatParameter
                    : undefined,
                })
            "
            :item-value="(basis) => basis.key"
            :sort-items="false"
            :searchable="false"
            return-object
          />
          <bm-number-q
            v-if="repeatParameterIsRelevant"
            :value.sync="internalSalesVisit.repeatParameter"
            :t="keys.sales_visit_repeat_parameter"
            :read-only="!isEditing"
            :min="1"
            :max="maxRepeatParameter"
            :rules="[$ruleSet.numberInRange(1, maxRepeatParameter)]"
            integral
          />
        </bm-form-flow-layout>
      </v-col>
    </v-row>
  </v-container>
</template>
